import { useEffect, useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { IChannelDTO, IChannelEntity, removeWhiteSpaceFromAllProperties } from '@rasayi-workspace/shared';
import { GetItem, PostItem, PutItem } from '@services';
import { BASE_QUERY_OPTIONS } from '@constants';
import { AddEditModalProps, ErrorResponse } from '@interfaces';

export const ChannelModalComponent = ({ onClose, onSubmit, editMode = false, id: channelId = '' }: AddEditModalProps<IChannelEntity>) => {
    const [form] = Form.useForm<Partial<IChannelDTO>>();
    const [messageApi, contextHolder] = message.useMessage();
    const [isValidForm, setIsValidForm] = useState(false);
    const [channel, setChannel] = useState<Partial<IChannelEntity>>();

    const { mutate: createChannel, isLoading } = useMutation<IChannelEntity, AxiosError>({
        mutationKey: ['createChannel'],
        mutationFn: async () =>
            PostItem<IChannelEntity, IChannelDTO>(
                'channel',
                removeWhiteSpaceFromAllProperties(form.getFieldsValue()) as Partial<IChannelDTO>
            ),
        onSuccess: async (item: IChannelEntity): Promise<void> => onSubmit(item),
        onError: (error: AxiosError) => {
            messageApi.open({
                type: 'error',
                content: (error?.response?.data as ErrorResponse)?.message || 'Contact support for details'
            });
        }
    });

    const { mutate: updateChannel, isLoading: isChannelUpdating } = useMutation<IChannelEntity, AxiosError>({
        mutationKey: ['updateChannel'],
        mutationFn: async () =>
            PutItem<IChannelEntity, IChannelDTO>(
                'channel',
                channelId,
                removeWhiteSpaceFromAllProperties(form.getFieldsValue()) as Partial<IChannelDTO>
            ),
        onSuccess: async (item: IChannelEntity): Promise<void> => onSubmit(item),
        onError: (error: AxiosError) => {
            messageApi.open({
                type: 'error',
                content: (error?.response?.data as ErrorResponse)?.message || 'Contact support for details'
            });
        }
    });

    const { refetch: fetchChannel, isFetching: isChannelFetching } = useQuery<IChannelEntity, AxiosError>({
        ...BASE_QUERY_OPTIONS,
        queryKey: ['getChannel'],
        queryFn: () => GetItem<IChannelEntity>(
            'channel',
            channelId || ''
        ),
        onSuccess: (result: IChannelEntity): void => {
            setChannel(channel);
            form.setFieldsValue({
                ...result,
            })
        },
        onError: (error) => messageApi.open({
            type: 'error',
            content: (error?.response?.data as ErrorResponse)?.message || 'Contact support for details'
        })
    });

    const updateFormFields = (property: string, value: string) => form.setFieldsValue({ [property]: value });

    const handleFormChange = () => {
        const fieldsTouched = form.isFieldsTouched(true);
        const hasErrors = form.getFieldsError().filter(({ errors }) => errors.length)
            .length > 0;

        setIsValidForm(fieldsTouched && !hasErrors);
    }

    const onOkAddButtonClickHandler = async () => await form.validateFields()
        .then(() => createChannel())
        .catch(() => setIsValidForm(false));

    const onOkEditButtonClickHandler = async () => await form.validateFields()
        .then(() => updateChannel())
        .catch(() => setIsValidForm(false));

    useEffect(() => {
        if (editMode)
            fetchChannel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {contextHolder}
            <Modal
                title={!editMode ? 'Add Channel' : 'Edit Channel'}
                open={true}
                confirmLoading={isLoading || isChannelFetching || isChannelUpdating}
                // okButtonProps={{ disabled: !isValidForm }}
                okText={!editMode ? 'Add Channel' : 'Edit Channel'}
                onOk={!editMode ? onOkAddButtonClickHandler : onOkEditButtonClickHandler}
                onCancel={() => onClose()}
                maskClosable={false}
                bodyStyle={{ maxHeight: 550, overflow: 'auto' }}
            >
                <Form
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    form={form}
                    onValuesChange={handleFormChange}
                >
                    <Form.Item
                        name='name'
                        label='Name'
                        rules={[
                            { required: true, message: 'Please enter the name' },
                            { min: 2, message: 'Atleast 2 characters' }
                        ]}
                    >
                        <Input onChange={(event) => updateFormFields('name', event?.target.value)} />
                    </Form.Item>
                    <Form.Item
                        name='description'
                        label='Description'
                        rules={[
                            { required: false, message: 'Please enter the description' },
                            // { min: 2, message: 'Atleast 2 characters' }
                        ]}
                    >
                        <Input onChange={(event) => updateFormFields('description', event?.target.value)} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
